import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { saveToken } from '@/lib/auth'

// Gateway session
export function useGatewaySession() {
  return useQuery({
    queryKey: ['gateway', 'session'],
    queryFn: api.gatewaySession,
    refetchInterval: 5 * 60_000,
    retry: false,
  })
}

export function useCurrentUser() {
  const { data } = useGatewaySession()
  return {
    user_id: data?.user_id ?? '',
    email: data?.email ?? '',
    display_name: data?.display_name ?? 'User',
    plan: data?.plan ?? 'free',
    tenant_id: data?.tenant_id ?? null,
    tenant_role: data?.tenant_role ?? null,
    permissions: data?.permissions ?? [] as string[],
  }
}

export function useSwitchTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tenantId: string) => api.switchTenant(tenantId),
    onSuccess: (data) => {
      if (data.token) {
        saveToken(data.token)
        qc.invalidateQueries()
      }
    },
  })
}

// Team
export function useTeam() {
  const { data: session } = useGatewaySession()
  return useQuery({
    queryKey: ['gateway', 'team'],
    queryFn: api.team,
    enabled: !!(session?.tenant_id),
  })
}

export function useInviteMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) => api.teamInvite(email, role),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'team'] }) },
  })
}

export function useUpdateMemberRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => api.teamUpdateRole(userId, role),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'team'] }) },
  })
}

export function useRemoveMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => api.teamRemove(userId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'team'] }) },
  })
}

// Deployment approvals
export function useDeploymentApprovals() {
  const { data: session } = useGatewaySession()
  return useQuery({
    queryKey: ['gateway', 'deployapprovals'],
    queryFn: api.deploymentApprovals,
    enabled: !!(session?.tenant_id),
    refetchInterval: 30_000,
  })
}

export function useApproveDeployment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ approvalId }: { approvalId: string }) => api.approveDeployment(approvalId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'deployapprovals'] }) },
  })
}

export function useRejectDeployment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ approvalId, reason }: { approvalId: string; reason?: string }) => api.rejectDeployment(approvalId, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'deployapprovals'] }) },
  })
}

// Slack
export function useSlackConfig() {
  const { data: session } = useGatewaySession()
  return useQuery({
    queryKey: ['gateway', 'slack'],
    queryFn: api.slackConfig,
    enabled: !!(session?.tenant_id),
  })
}

export function useSetSlackWebhook() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { webhook_url: string; channel_name?: string; notify_deploy?: boolean; notify_approval?: boolean }) =>
      api.setSlackWebhook(params),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'slack'] }) },
  })
}

export function useDisconnectSlack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.disconnectSlack(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'slack'] }) },
  })
}

// Billing
export function useBillingStatus() {
  return useQuery({
    queryKey: ['gateway', 'billing'],
    queryFn: api.billingStatus,
    refetchInterval: 5 * 60_000,
  })
}

export function useBillingCheckout() {
  return useMutation({
    mutationFn: ({ planId, billingCycle }: { planId: string; billingCycle: string }) =>
      api.billingCheckout(planId, billingCycle),
  })
}

export function useBillingPortal() {
  return useMutation({
    mutationFn: () => api.billingPortal(),
  })
}

// GitHub
export function useGitHubStatus() {
  return useQuery({
    queryKey: ['gateway', 'github', 'status'],
    queryFn: api.githubStatus,
    refetchInterval: 60_000,
    retry: false,
  })
}

export function useGitHubInitiate() {
  return useMutation({
    mutationFn: () => api.githubInitiate(),
  })
}

export function useGitHubDisconnect() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.githubDisconnect(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'github'] }) },
  })
}

export function useGitHubRepos() {
  const { data: status } = useGitHubStatus()
  return useQuery({
    queryKey: ['gateway', 'github', 'repos'],
    queryFn: api.githubRepos,
    enabled: !!(status?.connected),
  })
}

export function useGitHubConnectPat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pat, githubHost }: { pat: string; githubHost?: string }) =>
      api.githubConnectPat(pat, githubHost),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gateway', 'github'] }) },
  })
}

// Existing hooks below
export function useHealth() {
  return useQuery({ queryKey: ['dashboard', 'health'], queryFn: api.health, refetchInterval: 30_000 })
}

export function useApprovals(poll = false) {
  const { data: session } = useGatewaySession()
  const principalId = session?.user_id ?? ''
  return useQuery({
    queryKey: ['dashboard', 'approvals', principalId],
    queryFn: api.approvals,
    enabled: !!principalId,
    refetchInterval: poll ? 10_000 : false,
  })
}

export function useApprovalResults() {
  const { data: session } = useGatewaySession()
  const principalId = session?.user_id ?? ''
  return useQuery({
    queryKey: ['dashboard', 'approval-results', principalId],
    queryFn: api.approvalResults,
    enabled: !!principalId,
    refetchInterval: 15_000,
  })
}

export function useWorkers() {
  return useQuery({ queryKey: ['dashboard', 'workers'], queryFn: api.workers, refetchInterval: 30_000 })
}

export function useAudit(limit?: number, filter?: string) {
  return useQuery({ queryKey: ['dashboard', 'audit', limit, filter], queryFn: () => api.audit(limit, filter) })
}

export function useSecurity() {
  return useQuery({ queryKey: ['dashboard', 'security'], queryFn: api.security, refetchInterval: 30_000 })
}

export function useTenants() {
  return useQuery({ queryKey: ['dashboard', 'tenants'], queryFn: api.tenants })
}

export function useIncidents() {
  return useQuery({ queryKey: ['dashboard', 'incidents'], queryFn: api.incidents, refetchInterval: 15_000 })
}

export function usePatches() {
  return useQuery({ queryKey: ['dashboard', 'patches'], queryFn: api.patches })
}

export function useMcpStats() {
  return useQuery({ queryKey: ['dashboard', 'mcp-stats'], queryFn: api.mcpStats })
}

export function useMcpCatalogue() {
  return useQuery({
    queryKey: ['dashboard', 'mcp-catalogue'],
    queryFn: async () => {
      const catalogue = await api.mcpCatalogueGet()
      const generated = catalogue.generated_at ? Date.parse(catalogue.generated_at) : NaN
      const snapshot_fresh = Number.isFinite(generated)
        && Date.now() - generated <= 24 * 60 * 60 * 1000
      return { ...catalogue, snapshot_fresh }
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

export function useIntelligence() {
  return useQuery({ queryKey: ['dashboard', 'intelligence'], queryFn: api.intelligence })
}

export function useMemory() {
  return useQuery({ queryKey: ['dashboard', 'memory'], queryFn: api.memory })
}

export function usePerformance() {
  return useQuery({ queryKey: ['dashboard', 'performance'], queryFn: api.performance, refetchInterval: 60_000 })
}

export function useResources() {
  return useQuery({ queryKey: ['dashboard', 'resources'], queryFn: api.resources })
}

export function useMemoryTree() {
  return useQuery({ queryKey: ['dashboard', 'memory-tree'], queryFn: api.memoryTree, staleTime: 30000 })
}

export function useBackups() {
  return useQuery({ queryKey: ['dashboard', 'backups'], queryFn: api.backups })
}

export function useMemoryFile(key: string | null) {
  return useQuery({
    queryKey: ['dashboard', 'memory-file', key],
    queryFn: () => api.memoryFile(key as string),
    enabled: !!key,
  })
}

export function useApprove() {
  const qc = useQueryClient()
  return useMutation({
    // Fixed 2026-08-15: this previously called api.approve(), which hits
    // POST /dashboard/approve -- an endpoint that updates an unrelated
    // action_requests D1 row by request_id, and does nothing to the actual
    // Tier 2/3 approval:{token} record the tool gate checks. It always
    // returned {success: true} regardless, since an UPDATE matching zero
    // rows is not an error -- so every click here appeared to succeed
    // while never actually approving or executing anything. Confirmed live:
    // GovernancePanel's Approval Queue and ChatPanel's inline approval
    // cards both call this same hook, both were affected. api.approveAction
    // is the endpoint that actually looks up the stored proposal by token
    // and executes it.
    mutationFn: ({ requestId }: { requestId: string; notes?: string }) => api.approveAction(requestId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'approvals'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'approval-results'] })
    },
    onError: (err) => { console.error('[Approve failed]', err) },
  })
}

export function useReject() {
  const qc = useQueryClient()
  return useMutation({
    // Same fix, same reason, as useApprove above -- api.reject() hit the
    // same disconnected action_requests pattern. api.denyAction correctly
    // deletes the actual pending approval:{token} record.
    mutationFn: ({ requestId }: { requestId: string; reason?: string }) => api.denyAction(requestId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'approvals'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'approval-results'] })
    },
    onError: (err) => { console.error('[Reject failed]', err) },
  })
}

export function useSession() {
  return useQuery({ queryKey: ['dashboard', 'session'], queryFn: api.session, refetchInterval: 30_000 })
}

export function useSessionStart() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { session_type: string; workers?: string[] | null; reason?: string; hours: number; totpCode: string }) => api.sessionStart(params),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'session'] }) },
    onError: (err) => { console.error('[SessionStart]', err) },
  })
}

export function useSessionEnd() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (totpCode: string) => api.sessionEnd(totpCode),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'session'] }) },
  })
}

export function useSessionModifyWorkers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { action: 'add' | 'remove'; worker: string; totpCode: string }) => api.sessionModifyWorkers(params),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'session'] }) },
  })
}

export function useR2List(prefix: string, limit?: number) {
  return useQuery({
    queryKey: ['dashboard', 'r2', 'list', prefix, limit],
    queryFn: () => api.r2List(prefix, limit),
    enabled: !!prefix,
  })
}

export function useR2Read(key: string | null) {
  return useQuery({
    queryKey: ['dashboard', 'r2', 'read', key],
    queryFn: () => api.r2Read(key!),
    enabled: !!key,
  })
}

export function useTokenUsage() {
  return useQuery({ queryKey: ['dashboard', 'token-usage'], queryFn: () => api.tokenUsage(), refetchInterval: 30000 })
}

export function useImages() {
  return useQuery({ queryKey: ['dashboard', 'images'], queryFn: () => api.images() })
}

export function useLibrary() {
  return useQuery({ queryKey: ['dashboard', 'library'], queryFn: () => api.library() })
}

export function useModelStatus() {
  // Refetches periodically -- this is meant to reflect live, current
  // configuration, not a value fixed at page load.
  return useQuery({ queryKey: ['dashboard', 'model-status'], queryFn: () => api.modelStatus(), refetchInterval: 60000 })
}

export function useActionLog(result?: string, action?: string, search?: string, limit?: number) {
  return useQuery({
    queryKey: ['dashboard', 'action-log', result, action, search, limit],
    queryFn: () => api.actionLog(result, action, search, limit),
  })
}

export function useExtensionActivities(filters:import('@/lib/api').ExtensionActivityFilters={}) {
  return useQuery({
    queryKey:['dashboard','extension-activities',filters],
    queryFn:()=>api.extensionActivities(filters),
    refetchInterval:10000,
    retry:1,
  })
}

export function useEvents(category?: string, worker?: string, eventType?: string, after?: string, before?: string, limit?: number) {
  return useQuery({ queryKey: ['dashboard', 'events', category, worker, eventType, after, before, limit], queryFn: () => api.events(category, worker, eventType, after, before, limit), refetchInterval: 30000 })
}

export function useTasks(status?: string, limit?: number) {
  return useQuery({
    queryKey: ['dashboard', 'tasks', status, limit],
    queryFn: () => api.tasks(status, limit),
  })
}

export function useAddTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { content: string }) => api.addTask(params.content),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'tasks'] }) },
  })
}

export function useCompleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { taskId: string }) => api.completeTask(params.taskId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'tasks'] }) },
  })
}

export function useReminders() {
  return useQuery({ queryKey: ['dashboard', 'reminders'], queryFn: () => api.reminders(), refetchInterval: 60_000 })
}

export function useSelfAuditSummary() {
  return useQuery({ queryKey: ['dashboard', 'self-audit', 'summary'], queryFn: () => api.selfAuditSummary(), refetchInterval: 60_000 })
}

export function useSelfAuditQueue(status?: string) {
  return useQuery({ queryKey: ['dashboard', 'self-audit', 'queue', status], queryFn: () => api.selfAuditQueue(status) })
}

export function useSelfAuditHistory() {
  return useQuery({ queryKey: ['dashboard', 'self-audit', 'history'], queryFn: () => api.selfAuditHistory() })
}

export function useSelfAuditCritical() {
  return useQuery({ queryKey: ['dashboard', 'self-audit', 'critical'], queryFn: () => api.selfAuditCritical() })
}

export function useRunSelfAudit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.selfAuditRun(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'self-audit'] }) },
  })
}

export function useApproveStagedFix() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { issueId: string }) => api.selfAuditApprove(params.issueId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'self-audit'] }) },
  })
}

export function useRejectStagedFix() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { issueId: string; reason?: string }) => api.selfAuditReject(params.issueId, params.reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'self-audit'] }) },
  })
}

export function useCommandCentre() {
  return useQuery({ queryKey: ['dashboard', 'command-centre'], queryFn: api.commandCentre, refetchInterval: 30_000 })
}

export function useRoutes() {
  return useQuery({ queryKey: ['dashboard', 'routes'], queryFn: api.routes, refetchInterval: 60_000 })
}

export function useLibrarianFindings(tenantId?: string, status?: string) {
  return useQuery({ queryKey: ['dashboard', 'librarian', 'findings', tenantId, status], queryFn: () => api.librarianFindings(tenantId, status) })
}

export function useLibrarianCrawl() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { tenantId?: string; sources?: string[] }) => api.librarianCrawl(params.tenantId, params.sources),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'librarian'] }) },
  })
}

export function useLibrarianResolve() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { findingId: string; resolution: string; tenantId?: string; updateContent?: string }) =>
      api.librarianResolve(params.findingId, params.resolution, params.tenantId, params.updateContent),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'librarian'] }) },
  })
}

export function useAdminTenants() {
  return useQuery({ queryKey: ['admin', 'tenants'], queryFn: api.adminTenants, refetchInterval: 30_000 })
}

export function useAdminStats() {
  return useQuery({ queryKey: ['admin', 'stats'], queryFn: api.adminStats, refetchInterval: 60_000 })
}

export function useAdminTenantDetail(tenantId: string) {
  return useQuery({ queryKey: ['admin', 'tenant', tenantId], queryFn: () => api.adminTenantDetail(tenantId), enabled: !!tenantId })
}

export function useAdminSuspendTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { tenantId: string; reason?: string }) => api.adminSuspendTenant(params.tenantId, params.reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin'] }) },
  })
}

export function useAdminActivateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tenantId: string) => api.adminActivateTenant(tenantId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin'] }) },
  })
}

export function useAdminChangePlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { tenantId: string; plan: string }) => api.adminChangePlan(params.tenantId, params.plan),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin'] }) },
  })
}

export function useToolPermissions() {
  return useQuery({ queryKey: ['dashboard', 'tool-permissions'], queryFn: api.toolPermissions })
}

export function useSetToolPermission() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ toolName, permission }: { toolName: string; permission: string }) => api.setToolPermission(toolName, permission),
    onMutate: async ({ toolName, permission }) => {
      await qc.cancelQueries({ queryKey: ['dashboard', 'tool-permissions'] })
      const previous = qc.getQueryData(['dashboard', 'tool-permissions'])
      qc.setQueryData<Awaited<ReturnType<typeof api.toolPermissions>>>(
        ['dashboard', 'tool-permissions'],
        old => old
          ? { ...old, tools: old.tools.map(t => (t.name === toolName ? { ...t, permission } : t)) }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(['dashboard', 'tool-permissions'], context.previous)
    },
    onSettled: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'tool-permissions'] }) },
  })
}

export function useSetGlobalPermissionMode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mode: string) => api.setToolMode(mode),
    onMutate: async (mode: string) => {
      await qc.cancelQueries({ queryKey: ['dashboard', 'tool-permissions'] })
      const previous = qc.getQueryData(['dashboard', 'tool-permissions'])
      qc.setQueryData<Awaited<ReturnType<typeof api.toolPermissions>>>(
        ['dashboard', 'tool-permissions'],
        old => old ? { ...old, global_mode: mode } : old,
      )
      return { previous }
    },
    onError: (_err, _mode, context) => {
      if (context?.previous) qc.setQueryData(['dashboard', 'tool-permissions'], context.previous)
    },
    onSettled: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'tool-permissions'] }) },
  })
}

export function useBuildReviews(verdict?: string, limit?: number) {
  return useQuery({
    queryKey: ['dashboard', 'build-reviews', verdict, limit],
    queryFn: () => api.buildReviews(verdict, limit),
    refetchInterval: 30_000,
  })
}

export function useBuildReviewCompliance() {
  return useQuery({
    queryKey: ['dashboard', 'build-review-compliance'],
    queryFn: api.buildReviewCompliance,
    refetchInterval: 60_000,
  })
}

export function useEngReview(status?: string, priority?: string) {
  return useQuery({
    queryKey: ['dashboard', 'eng-review', status, priority],
    queryFn: () => api.engReview(status, priority),
    refetchInterval: 60_000,
  })
}

export function useEngReviewAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ karId, action, notes, snoozeUntil }: { karId: string; action: string; notes?: string; snoozeUntil?: string }) =>
      api.engReviewAction(karId, action, notes, snoozeUntil),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'eng-review'] }) },
  })
}

export function useOpsBridgeFeatures(params?: { category?: string }) {
  return useQuery({
    queryKey: ['dashboard', 'opsbridge-features', params],
    queryFn: () => api.opsbridgeFeatures(params),
    refetchInterval: 60_000,
  })
}

export function usePlatformFeatures(params?: { category?: string; criticality?: string; verification_status?: string }) {
  return useQuery({
    queryKey: ['dashboard', 'platform-features', params],
    queryFn: () => api.platformFeatures(params),
    refetchInterval: 60_000,
  })
}

export function usePlatformFeatureById(id: string | null) {
  return useQuery({
    queryKey: ['dashboard', 'platform-feature', id],
    queryFn: () => api.platformFeatureById(id!),
    enabled: !!id,
  })
}

export function useVerifyPlatformFeature() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.verifyPlatformFeature(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'platform-features'] }) },
  })
}

export function useUpdatePlatformFeatureStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: string }) =>
      api.updatePlatformFeatureStatus(id, status, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'platform-features'] }) },
  })
}

// Cloudflare connection (second account)
export function useCfConnectionStatus() {
  return useQuery({ queryKey: ['dashboard', 'cf-connection'], queryFn: api.cfConnectionStatus, refetchInterval: 60_000 })
}

export function useGithubStatus() {
  return useQuery({ queryKey: ['dashboard', 'github-status'], queryFn: api.githubStatus, refetchInterval: 60_000 })
}

export function useWordpressDeepStatus() {
  return useQuery({ queryKey: ['dashboard', 'wordpress-deep-status'], queryFn: api.wordpressDeepStatus, refetchInterval: 60_000 })
}

export function useVpsResourceUsage() {
  return useQuery({ queryKey: ['dashboard', 'vps-resource-usage'], queryFn: api.vpsResourceUsage, refetchInterval: 30_000 })
}

export function useDeploymentsByDay() {
  return useQuery({ queryKey: ['dashboard', 'deployments-by-day'], queryFn: api.deploymentsByDay, refetchInterval: 60_000 })
}

export function useCfConnectionStore() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { account_id: string; api_token: string }) => api.cfConnectionStore(params),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'cf-connection'] }) },
  })
}

export function useCfConnectionDisconnect() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.cfConnectionDisconnect(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'cf-connection'] }) },
  })
}

export function usePipelineQueue(status?: string, stage?: string, limit?: number) {
  return useQuery({
    queryKey: ['dashboard', 'pipeline', 'queue', status, stage, limit],
    queryFn: () => api.pipelineQueue(status, stage, limit),
    refetchInterval: 15_000,
  })
}

export function usePipelineItem(id: string | null) {
  return useQuery({
    queryKey: ['dashboard', 'pipeline', 'item', id],
    queryFn: () => api.pipelineItem(id!),
    enabled: !!id,
    refetchInterval: 10_000,
  })
}

export function usePipelineApprove() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note, engine }: { id: string; note?: string; engine?: string }) =>
      api.pipelineApprove(id, note, engine),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'pipeline'] })
    },
  })
}

export function usePipelineReject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) =>
      api.pipelineReject(id, message),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'pipeline'] })
    },
  })
}

export function useBrowserLive() {
  return useQuery({
    queryKey: ['dashboard', 'browser', 'live'],
    queryFn: () => api.browserLive(),
    refetchInterval: 3000,
    retry: false,
  })
}

// One-time resolve of the live video WebSocket URL -- no polling interval,
// unlike useBrowserLive above, since once the WebSocket itself connects it
// receives real, continuous updates on its own. This hook only needs to
// re-run when the component using it explicitly asks (e.g. the session
// changed, or the previous connection was lost).
export function useBrowserLiveVideo(enabled: boolean) {
  return useQuery({
    queryKey: ['dashboard', 'browser', 'live-video'],
    queryFn: () => api.browserLiveVideo(),
    enabled,
    refetchInterval: enabled ? 10000 : false,
    retry: false,
    staleTime: 0,
  })
}

export function usePipelinePause() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.pipelinePause(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'pipeline'] })
    },
  })
}

export function usePipelineMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) =>
      api.pipelineMessage(id, message),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'pipeline', 'item', vars.id] })
    },
  })
}

export function usePipelineRerun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, stage, note }: { id: string; stage: string; note?: string }) =>
      api.pipelineRerun(id, stage, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'pipeline'] })
    },
  })
}

export function usePlatformModel() {
  return useQuery({
    queryKey: ['dashboard', 'platform-model'],
    queryFn: api.platformModelGet,
    staleTime: 60_000,
  })
}

export function usePlatformModelVersions() {
  return useQuery({
    queryKey: ['dashboard', 'platform-model', 'versions'],
    queryFn: api.platformModelVersions,
    staleTime: 60_000,
  })
}

export function usePlatformModelRefresh() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (changedWorker?: string) => api.platformModelRefresh(changedWorker),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'platform-model'] })
    },
  })
}


export function usePipelineTrigger() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ideaNumber?: number) => api.pipelineTrigger(ideaNumber),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'pipeline'] })
    },
  })
}
